import { isExcludedFromActive } from "./engineerStatus";

function n(value) {
  return Number(value || 0) || 0;
}

function clean(value, fallback = "") {
  const text = String(value || "").trim();
  return text || fallback;
}

function normalizeName(value) {
  return clean(value, "unknown").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function normalizeDisplayName(value) {
  return clean(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function looksLikeAlias(left, right) {
  const a = normalizeName(left);
  const b = normalizeName(right);
  if (!a || !b || a === "unknown" || b === "unknown") return false;
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 4) return false;
  return a.slice(0, 5) === b.slice(0, 5) && a.slice(-6) === b.slice(-6);
}

function slug(value) {
  return clean(value, "unknown")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function rowMonth(row, fallback) {
  return clean(row?.month || row?.month_key || fallback, "Unknown");
}

function teamValue(value) {
  return clean(value, "Unknown");
}

function fmt(value, digits = 1) {
  return n(value).toFixed(digits);
}

const SEVERITY_ORDER = { critical: 0, warning: 1, info: 2 };
const CATEGORY_ORDER = {
  "Rota days": 0,
  Rota: 1,
  "Duplicate summary": 2,
  "Summary detail": 3,
  "Alias coverage": 4,
  "Team match": 5,
  "Timestamp coverage": 6,
  "Grade coverage": 7,
};

function makeIssue(fields) {
  return {
    id: fields.id,
    severity: fields.severity,
    category: fields.category,
    month: clean(fields.month, "Unknown"),
    engineer: clean(fields.engineer, "All engineers"),
    team: teamValue(fields.team),
    summary: clean(fields.summary),
    detail: clean(fields.detail),
    action: clean(fields.action),
    evidence: clean(fields.evidence),
  };
}

function addIssue(issues, fields) {
  issues.push(makeIssue(fields));
}

// App.jsx groups History metrics by an alias-aware canonical key, so "DANIELHG"
// and "Daniel Hardinge-Grant" are one engineer there. This report has to group the
// same way or it disagrees with the numbers on screen: duplicate rows linked only
// by an alias go unreported, and detail/rota lookups keyed by the canonical name
// miss rows stored under an alias.
const IDENTITY_RESOLVER = {
  key: (value) => normalizeName(value),
  name: (value) => clean(value, "Unknown"),
};

function buildAliasResolver(aliasRows) {
  const index = {};
  (aliasRows || []).forEach((row) => {
    const canonical = clean(row?.engineer);
    const canonicalKey = normalizeName(canonical);
    if (!canonical || !canonicalKey || canonicalKey === "unknown") return;
    const entry = { key: canonicalKey, name: canonical };
    index[canonicalKey] = entry;
    aliasList(row).forEach((alias) => {
      const aliasKey = normalizeName(alias);
      if (aliasKey && aliasKey !== "unknown") index[aliasKey] = entry;
    });
  });
  if (!Object.keys(index).length) return IDENTITY_RESOLVER;
  return {
    key: (value) => index[normalizeName(value)]?.key || normalizeName(value),
    name: (value) =>
      index[normalizeName(value)]?.name || clean(value, "Unknown"),
  };
}

function summaryGroups(summaries, fallbackMonth, resolver = IDENTITY_RESOLVER) {
  const groups = {};
  (summaries || []).forEach((summary) => {
    const month = rowMonth(summary, fallbackMonth);
    const engineer = resolver.name(summary.engineer);
    const key = `${month}|${resolver.key(summary.engineer)}`;
    if (!groups[key])
      groups[key] = {
        month,
        engineer,
        team: summary.team || "",
        summaries: [],
        summaryParts: 0,
        hasPartTotals: false,
      };
    groups[key].summaries.push(summary);
    if (Object.prototype.hasOwnProperty.call(summary, "total_parts")) {
      groups[key].summaryParts += n(summary.total_parts);
      groups[key].hasPartTotals = true;
    }
    if (!groups[key].team && summary.team) groups[key].team = summary.team;
  });
  return groups;
}

function detailGroups(records, fallbackMonth, resolver = IDENTITY_RESOLVER) {
  const groups = {};
  (records || []).forEach((record) => {
    const month = rowMonth(record, fallbackMonth);
    const engineer = resolver.name(record.engineer);
    const key = `${month}|${resolver.key(record.engineer)}`;
    if (!groups[key])
      groups[key] = {
        month,
        engineer,
        teams: {},
        count: 0,
        // Distinct completed dates are the last available evidence of how many
        // days an engineer actually worked, used when the rota and the saved
        // Repair Centre figure both come back empty.
        workedDates: new Set(),
      };
    groups[key].count += 1;
    const completedDay = clean(record.completed_at).slice(0, 10);
    if (completedDay) groups[key].workedDates.add(completedDay);
    const team = clean(record.team);
    if (team) groups[key].teams[team] = (groups[key].teams[team] || 0) + 1;
  });
  return groups;
}

// rota_entries stores `year` and a numeric `month` (1-12). Passing such a row to
// rowMonth yields "4", which never matches the "2026-04" keys used for summary
// and detail rows, so every rota lookup silently missed.
function rotaRowMonth(row, fallback) {
  const explicit = clean(row?.month_key);
  if (explicit) return explicit;
  const year = Number(row?.year);
  const month = Number(row?.month);
  if (year && month >= 1 && month <= 12)
    return `${year}-${String(month).padStart(2, "0")}`;
  return rowMonth(row, fallback);
}

function rotaGroups(rotaRows, fallbackMonth, resolver = IDENTITY_RESOLVER) {
  const groups = {};
  (rotaRows || []).forEach((row) => {
    const month = rotaRowMonth(row, fallbackMonth);
    const engineer = resolver.name(row.engineer);
    const key = `${month}|${resolver.key(row.engineer)}`;
    groups[key] = {
      month,
      engineer,
      team: clean(row.department || row.team || row.section),
    };
  });
  return groups;
}

function aliasCovers(aliasRows, left, right) {
  const a = normalizeName(left);
  const b = normalizeName(right);
  return (aliasRows || []).some((row) => {
    const names = [
      row.engineer,
      row.name,
      row.display_name,
      row.canonical_name,
      row.alias,
      ...(Array.isArray(row.aliases) ? row.aliases : []),
    ]
      .filter(Boolean)
      .map(normalizeName);
    return names.includes(a) && names.includes(b);
  });
}

function aliasList(row) {
  return (row?.aliases || [])
    .map((entry) => (typeof entry === "string" ? entry : entry?.alias))
    .filter(Boolean);
}

function collectHistoryNames(pack) {
  const names = {};
  const add = (name, source) => {
    const raw = clean(name);
    const key = normalizeName(raw);
    if (!raw || !key || key === "unknown") return;
    if (!names[key])
      names[key] = { name: raw, compact: key, count: 0, sources: new Set() };
    names[key].count += 1;
    names[key].sources.add(source);
    names[key].name = names[key].name === raw ? names[key].name : names[key].name;
  };
  (pack?.summaries || []).forEach((row) => add(row.engineer, "summary"));
  (pack?.records || []).forEach((row) => add(row.engineer, "repair detail"));
  (pack?.uploadNames || []).forEach((name) => add(name, "repair upload"));
  (pack?.uploadRows || []).forEach((row) => {
    add(
      row?.engineer || row?.repairer || row?.person || row?.name,
      "repair upload",
    );
  });
  return Object.values(names);
}

function buildRosterTargets(pack, roster) {
  const targets = {};
  const add = (row, source) => {
    const engineer = clean(row?.engineer);
    const key = normalizeName(engineer);
    if (!engineer || !key || key === "unknown") return;
    if (!targets[key])
      targets[key] = {
        engineer,
        compact: key,
        aliases: [],
        sources: new Set(),
      };
    targets[key].sources.add(source);
    aliasList(row).forEach((alias) => {
      if (!targets[key].aliases.includes(alias)) targets[key].aliases.push(alias);
    });
  };
  (roster || []).forEach((row) => add(row, "roster"));
  (pack?.rota || []).forEach((row) => add(row, "rota"));
  return Object.values(targets);
}

function nameTokens(value) {
  return clean(value)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function initialSurnameCompact(value) {
  const tokens = nameTokens(value);
  if (tokens.length < 2) return "";
  return `${tokens[0][0]}${tokens.slice(1).join("")}`;
}

function firstNameLastInitialCompact(value) {
  const tokens = nameTokens(value);
  if (tokens.length < 2) return "";
  return `${tokens[0]}${tokens[1][0]}`;
}

function firstNameSurnameInitialsCompact(value) {
  const tokens = nameTokens(value);
  if (tokens.length < 2) return "";
  return `${tokens[0]}${tokens.slice(1).map((token) => token[0]).join("")}`;
}

function aliasAlreadyPresent(target, aliasName) {
  const aliasKey = normalizeName(aliasName);
  const sameDisplayName =
    normalizeDisplayName(target.engineer) === normalizeDisplayName(aliasName);
  return (
    sameDisplayName ||
    target.aliases.some((alias) => normalizeName(alias) === aliasKey)
  );
}

function candidateReason(alias, target) {
  if (alias.compact === target.compact) return "Compact name match";
  if (alias.compact === initialSurnameCompact(target.engineer))
    return "Initial and surname match";
  if (alias.compact === firstNameLastInitialCompact(target.engineer))
    return "First name and surname initial match";
  if (alias.compact === firstNameSurnameInitialsCompact(target.engineer))
    return "First name and surname initials match";
  if (looksLikeAlias(alias.name, target.engineer)) return "Close name match";
  return "";
}

export function buildEngineerAliasSuggestions(pack, roster = [], options = {}) {
  const statusIndex = options.statusIndex;
  const aliases = collectHistoryNames(pack).filter(
    (alias) => !isExcludedFromActive(alias.name, statusIndex),
  );
  const targets = buildRosterTargets(pack, roster).filter(
    (target) => !isExcludedFromActive(target.engineer, statusIndex),
  );
  const suggestions = [];

  aliases.forEach((alias) => {
    const candidates = targets
      .map((target) => ({ target, reason: candidateReason(alias, target) }))
      .filter((entry) => entry.reason);
    // If any candidate already owns this alias — saved on it, or because the
    // alias is that engineer's own display name — the name is already resolved.
    // Filtering that target out instead of stopping here is what let a rota
    // rename ("Chris Stamataras" -> "Christos Stamataras") leave a stale target
    // as the only remaining candidate, which then auto-applied and split one
    // engineer into two.
    if (candidates.some(({ target }) => aliasAlreadyPresent(target, alias.name)))
      return;
    const unsaved = candidates;
    if (!unsaved.length) return;

    const chosen = unsaved[0];
    const ambiguous = unsaved.length > 1;
    suggestions.push({
      id: `alias-${slug(chosen.target.engineer)}-${slug(alias.name)}`,
      engineer: chosen.target.engineer,
      alias: alias.name,
      confidence: ambiguous ? "review" : "high",
      autoApply: !ambiguous,
      reason: ambiguous ? "Ambiguous match" : chosen.reason,
      evidence: ambiguous
        ? `Could match ${unsaved.map(({ target }) => target.engineer).join(", ")}.`
        : `Seen ${alias.count} ${alias.count === 1 ? "time" : "times"} in History rows; target appears in ${[...chosen.target.sources].join("/")}.`,
    });
  });

  return suggestions.sort((a, b) => {
    if (a.autoApply !== b.autoApply) return a.autoApply ? -1 : 1;
    const byEngineer = a.engineer.localeCompare(b.engineer);
    if (byEngineer) return byEngineer;
    return a.alias.localeCompare(b.alias);
  });
}

export function buildRepairUploadAliasTargets({
  activeMonth = "",
  repairRotaMonth = "",
  repairRotaEntries = [],
  engineerAliases = [],
  statusIndex = null,
} = {}) {
  const aliasesByEngineer = {};
  (engineerAliases || []).forEach((row) => {
    const key = normalizeName(row?.engineer);
    if (!key || isExcludedFromActive(row?.engineer, statusIndex)) return;
    aliasesByEngineer[key] = aliasList(row).filter(
      (alias) => !isExcludedFromActive(alias, statusIndex),
    );
  });

  const monthEntries =
    repairRotaMonth === activeMonth && (repairRotaEntries || []).length
      ? repairRotaEntries
      : [];
  const sourceRows = monthEntries.length ? monthEntries : engineerAliases || [];

  return (sourceRows || [])
    .filter((entry) => !isExcludedFromActive(entry?.engineer, statusIndex))
    .map((entry) => {
      const key = normalizeName(entry?.engineer);
      return {
        engineer: entry.engineer,
        section: entry.section || "",
        year: entry.year,
        month: entry.month,
        aliases: monthEntries.length
          ? aliasesByEngineer[key] || []
          : aliasList(entry).filter((alias) => !isExcludedFromActive(alias, statusIndex)),
      };
    })
    .filter((entry) => clean(entry.engineer));
}

export function countVerifiedEngineerAliases(roster = [], groupedAliases = {}) {
  const targets = {};
  (roster || []).forEach((row) => {
    const engineerKey = normalizeName(row?.engineer);
    if (!engineerKey || engineerKey === "unknown") return;
    targets[engineerKey] = new Set(
      aliasList(row).map((alias) => normalizeName(alias)).filter(Boolean),
    );
  });

  let count = 0;
  Object.entries(groupedAliases || {}).forEach(([engineer, aliases]) => {
    const targetAliases = targets[normalizeName(engineer)];
    if (!targetAliases) return;
    (aliases || []).forEach((alias) => {
      if (targetAliases.has(normalizeName(alias))) count += 1;
    });
  });
  return count;
}

function dominantTeam(teams) {
  return Object.entries(teams || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
}

function issueSort(a, b) {
  const severity = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
  if (severity) return severity;
  const category =
    (CATEGORY_ORDER[a.category] ?? 99) - (CATEGORY_ORDER[b.category] ?? 99);
  if (category) return category;
  const month = a.month.localeCompare(b.month);
  if (month) return month;
  return a.engineer.localeCompare(b.engineer);
}

export function buildHistoryConsistencyReport(metrics, pack, options = {}) {
  const issues = [];
  const fallbackMonth = pack?.month || options.month || "Unknown";
  const summaries = pack?.summaries || [];
  const records = pack?.records || [];
  const rota = pack?.rota || [];
  const aliases = options.aliasRows || [];
  const statusIndex = options.statusIndex;
  const resolver = buildAliasResolver(aliases);
  const summaryByKey = summaryGroups(summaries, fallbackMonth, resolver);
  const detailByKey = detailGroups(records, fallbackMonth, resolver);
  const rotaByKey = rotaGroups(rota, fallbackMonth, resolver);

  (metrics?.engineers || []).forEach((engineer) => {
    // Leavers have no rota row by design, and admin staff are not repair
    // engineers at all. Reporting either as missing rota, or as day/team
    // mismatches, is noise the user cannot act on.
    if (isExcludedFromActive(engineer.engineer, statusIndex)) return;
    const engineerKey = resolver.key(engineer.engineer);
    const month =
      rowMonth(
        summaries.find(
          (summary) => resolver.key(summary.engineer) === engineerKey,
        ),
        fallbackMonth,
      ) || fallbackMonth;
    const key = `${month}|${engineerKey}`;
    const savedDays = n(engineer.daysWorked);
    const rotaDays = n(engineer.rotaWorkedDays);
    // A month still running has rota days for dates that have not happened yet,
    // so the whole-month figure cannot be compared against a saved snapshot taken
    // partway through. App.jsx tracks the elapsed rota for exactly this case; use
    // it when it disagrees with the full month, which is what marks the month as
    // still in progress.
    const elapsedRotaDays = n(engineer.elapsedRotaWorkedDays);
    const monthInProgress =
      Object.prototype.hasOwnProperty.call(engineer, "elapsedRotaWorkedDays") &&
      Math.abs(elapsedRotaDays - rotaDays) >= 0.01;
    const comparableRotaDays = monthInProgress ? elapsedRotaDays : rotaDays;
    const difference = Math.abs(savedDays - comparableRotaDays);

    // Days worked can come from three places, in order of authority: the rota,
    // the figure the Repair Centre calculator saved on the summary, and the
    // distinct completed dates on the detail rows. Only when all three are empty
    // is there genuinely no basis for this engineer's daily averages — anything
    // else is the documented fallback working as intended.
    const detailWorkedDays = detailByKey[key]?.workedDates?.size || 0;
    const hasDayBasis = rotaDays > 0 || savedDays > 0 || detailWorkedDays > 0;

    if (!hasDayBasis && n(engineer.totalParts) > 0 && rotaByKey[key]) {
      // The rota row exists, it just contains no worked days — a full month of
      // leave or sickness. That is not an alias problem, so it must not be
      // reported as one.
      addIssue(issues, {
        id: `rota-no-worked-days-${slug(month)}-${slug(engineer.engineer)}`,
        severity: "warning",
        category: "Rota",
        month,
        engineer: engineer.engineer,
        team: engineer.team,
        summary: "Rota shows no worked days",
        detail:
          "This engineer has a rota row for the month but it contains no worked days, so daily averages fall back to saved days.",
        action:
          "Confirm the rota codes for this month if these parts were worked on a working day.",
        evidence: `${n(engineer.totalParts).toLocaleString()} parts closed against zero rota worked days.`,
      });
    } else if (!hasDayBasis && n(engineer.totalParts) > 0) {
      addIssue(issues, {
        id: `missing-rota-${slug(month)}-${slug(engineer.engineer)}`,
        severity: "warning",
        category: "Rota",
        month,
        engineer: engineer.engineer,
        team: engineer.team,
        summary: "No rota match found",
        detail: "This engineer has saved History output but no matched rota row.",
        action: "Add an engineer alias or upload the correct rota for this month.",
        evidence: `${n(engineer.totalParts).toLocaleString()} parts use saved days instead of rota days.`,
      });
    } else if (comparableRotaDays > 0 && savedDays > 0 && difference >= 0.01) {
      addIssue(issues, {
        id: `day-mismatch-${slug(month)}-${slug(engineer.engineer)}`,
        severity: difference >= 1 ? "critical" : "warning",
        category: "Rota days",
        month,
        engineer: engineer.engineer,
        team: engineer.team,
        summary: "Saved days differ from rota days",
        detail: `Saved days are ${fmt(savedDays)} but rota-adjusted days are ${fmt(comparableRotaDays)}.`,
        action: "Check rota import or re-save this month after confirming worked days.",
        evidence: `${fmt(difference)} day difference affects daily averages and bonus pace.`,
      });
    }

    const detail = detailByKey[key];
    const detailTeam = dominantTeam(detail?.teams);
    const summaryTeam = clean(summaryByKey[key]?.team || engineer.team);
    const rotaTeam = clean(rotaByKey[key]?.team);
    if (
      detailTeam &&
      summaryTeam &&
      detailTeam !== summaryTeam &&
      detailTeam !== "Unknown"
    ) {
      addIssue(issues, {
        id: `team-mismatch-${slug(month)}-${slug(engineer.engineer)}`,
        severity: "warning",
        category: "Team match",
        month,
        engineer: engineer.engineer,
        team: summaryTeam,
        summary: "Team values disagree",
        detail: `History uses ${summaryTeam}, while most detail rows use ${detailTeam}${rotaTeam ? ` and rota says ${rotaTeam}` : ""}.`,
        action: "Check the grading reference, rota section, and saved repair upload team values.",
        evidence: `${detail?.count || 0} detail rows inspected.`,
      });
    }
  });

  Object.values(summaryByKey).forEach((group) => {
    if (isExcludedFromActive(group.engineer, statusIndex)) return;
    if (group.summaries.length > 1) {
      addIssue(issues, {
        id: `duplicate-summary-${slug(group.month)}-${slug(group.engineer)}`,
        severity: "critical",
        category: "Duplicate summary",
        month: group.month,
        engineer: group.engineer,
        team: group.team,
        summary: "Duplicate monthly summaries",
        detail: "More than one saved summary row exists for this engineer and month.",
        action: "Re-save the month after confirming the intended engineer name.",
        evidence: `${group.summaries.length} summary rows found.`,
      });
    }

    const detail = detailByKey[`${group.month}|${resolver.key(group.engineer)}`];
    if (
      group.hasPartTotals &&
      detail &&
      Math.abs(group.summaryParts - detail.count) >= 1
    ) {
      addIssue(issues, {
        id: `summary-detail-gap-${slug(group.month)}-${slug(group.engineer)}`,
        severity: "critical",
        category: "Summary detail",
        month: group.month,
        engineer: group.engineer,
        team: group.team,
        summary: "Summary total differs from detail rows",
        detail: `Summary rows total ${group.summaryParts.toLocaleString()} parts, detail rows contain ${detail.count.toLocaleString()}.`,
        action: "Re-save the affected period from the Repair Centre calculator.",
        evidence: `${Math.abs(group.summaryParts - detail.count).toLocaleString()} part gap.`,
      });
    }
  });

  Object.values(rotaByKey).forEach((rotaRow) => {
    if (isExcludedFromActive(rotaRow.engineer, statusIndex)) return;
    const compactMatches = (metrics?.engineers || []).filter((engineer) => {
      const engineerName = clean(engineer.engineer);
      return (
        engineerName &&
        !isExcludedFromActive(engineerName, statusIndex) &&
        engineerName !== rotaRow.engineer &&
        looksLikeAlias(engineerName, rotaRow.engineer) &&
        !aliasCovers(aliases, engineerName, rotaRow.engineer)
      );
    });
    compactMatches.forEach((engineer) => {
      addIssue(issues, {
        id: `weak-alias-${slug(rotaRow.month)}-${slug(engineer.engineer)}`,
        severity: "warning",
        category: "Alias coverage",
        month: rotaRow.month,
        engineer: engineer.engineer,
        team: engineer.team || rotaRow.team,
        summary: "Possible alias cleanup",
        detail: `${engineer.engineer} and ${rotaRow.engineer} normalize to the same name but are written differently.`,
        action: "Add a formal Engineer Manager alias so future uploads resolve cleanly.",
        evidence: "Name differs only by spacing, case, or punctuation.",
      });
    });
  });

  const totalParts = n(metrics?.totalParts);
  const timestampMissing = n(metrics?.missing?.timestamp);
  const timestampCoverage = totalParts
    ? (n(metrics?.datedRecords) / totalParts) * 100
    : 100;
  if (totalParts > 0 && (timestampMissing > 0 || timestampCoverage < 75)) {
    addIssue(issues, {
      id: `timestamp-coverage-${slug(fallbackMonth)}`,
      severity: "warning",
      category: "Timestamp coverage",
      month: fallbackMonth,
      engineer: "All engineers",
      team: "All teams",
      summary: "Low timestamp coverage",
      detail: `${fmt(timestampCoverage)}% of visible parts have completed timestamps.`,
      action: "Prefer newer KPI exports with completed timestamps before relying on daily/hourly charts.",
      evidence: `${timestampMissing.toLocaleString()} rows are missing timestamps.`,
    });
  }

  const missingGrade = n(metrics?.missing?.grade);
  const ungradedParts = (metrics?.engineers || []).reduce(
    (sum, engineer) => sum + n(engineer.grades?.UNGRADED),
    0,
  );
  if (missingGrade > 0 || ungradedParts > 0) {
    addIssue(issues, {
      id: `grade-coverage-${slug(fallbackMonth)}`,
      severity: "warning",
      category: "Grade coverage",
      month: fallbackMonth,
      engineer: "All engineers",
      team: "All teams",
      summary: "Ungraded completed parts",
      detail: "Some completed parts do not have a resolved grade.",
      action: "Use Parts Grading to fix the highest-volume ungraded parts.",
      evidence: `${Math.max(missingGrade, ungradedParts).toLocaleString()} ungraded parts detected.`,
    });
  }

  const sortedIssues = issues.sort(issueSort);
  const counts = sortedIssues.reduce(
    (acc, issue) => {
      acc[issue.severity] += 1;
      return acc;
    },
    { critical: 0, warning: 0, info: 0, clean: 0 },
  );
  counts.clean = sortedIssues.length ? 0 : 1;

  return {
    issues: sortedIssues,
    counts,
    categories: [...new Set(sortedIssues.map((issue) => issue.category))].sort(),
  };
}
